import type { SaveData } from './save';
import type { ReplayData } from './replay';

/**
 * CLOUD BACKEND INTERFACE
 * The game only talks to this interface. `LocalBackend` is used when the cloud
 * configuration is absent (fully offline). `FirebaseBackend`
 * (firebaseBackend.ts) is loaded dynamically only when VITE_FIREBASE_* env vars
 * are present. It uses Firebase for identity/saves and the same-origin Pages
 * API for D1-backed leaderboards.
 */
export type LeaderboardKind = 'fastestClear' | 'highestEra' | 'highestScore' | 'daily';

export interface LeaderboardEntry {
  uid: string;
  name: string;
  value: number;
  seed: number;
  at: number;
  meta?: Record<string, number>;
  hasReplay?: boolean;
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  page: number;
  pageSize: number;
  total: number;
  ownEntry?: LeaderboardEntry;
  ownRank?: number;
}

export interface RunSubmission {
  seed: number;
  dailyKey: string | null;
  score: number;
  erasCleared: number;
  bossesKilled: number;
  highestEra: number;
  kills: number;
  level: number;
  timeSurvivedSec: number;
  victory: boolean;
  paradox: number;
  playerCount: number;
  startEra: number;
  /** Ordered upgrade ids taken (anti-cheat sanity: count must fit level). */
  upgrades: string[];
  character: string;
  equipment: string[];
  version: string;
  replay?: ReplayData;
}

export interface UserInfo {
  uid: string;
  name: string;
  anonymous: boolean;
}

export interface CloudBackend {
  readonly kind: 'local' | 'firebase';
  init(): Promise<void>;
  currentUser(): UserInfo | null;
  onAuthChange(cb: (u: UserInfo | null) => void): () => void;
  signInAnonymously(): Promise<UserInfo>;
  signInWithGoogle(): Promise<UserInfo>;
  signOut(): Promise<void>;
  loadSave(): Promise<SaveData | null>;
  storeSave(data: SaveData): Promise<void>;
  submitRun(run: RunSubmission): Promise<{ accepted: boolean; reason?: string; score?: number }>;
  fetchLeaderboard(kind: LeaderboardKind, dailyKey?: string, page?: number, pageSize?: number): Promise<LeaderboardPage>;
  fetchReplay(kind: LeaderboardKind, uid: string, dailyKey?: string, seed?: number): Promise<ReplayData | null>;
  /** Firebase ID token for same-origin API calls (online co-op signalling); null when offline or signed out. */
  idToken(): Promise<string | null>;
}

/** Offline backend: no accounts, no leaderboards (keeps a local top list for the UI). */
export class LocalBackend implements CloudBackend {
  readonly kind = 'local' as const;
  private boards: Record<string, LeaderboardEntry[]> = {};
  idToken(): Promise<string | null> {
    return Promise.resolve(null);
  }
  private replays: Record<string, ReplayData> = {};

  async init(): Promise<void> {
    try {
      this.boards = JSON.parse(localStorage.getItem('chronofall.localboards') ?? '{}');
      this.replays = JSON.parse(localStorage.getItem('chronofall.localreplays') ?? '{}');
    } catch {
      this.boards = {};
    }
  }
  currentUser(): UserInfo | null {
    return null;
  }
  onAuthChange(): () => void {
    return () => {};
  }
  async signInAnonymously(): Promise<UserInfo> {
    throw new Error('Cloud features are not configured (see README: Online mode)');
  }
  async signInWithGoogle(): Promise<UserInfo> {
    throw new Error('Cloud features are not configured (see README: Online mode)');
  }
  async signOut(): Promise<void> {}
  async loadSave(): Promise<SaveData | null> {
    return null;
  }
  async storeSave(): Promise<void> {}
  async submitRun(run: RunSubmission): Promise<{ accepted: boolean; reason?: string; score?: number }> {
    if (run.dailyKey) {
      const existing = this.boards['daily:' + run.dailyKey]?.find((entry) => entry.uid === 'local');
      if (existing) return { accepted: false, reason: 'daily attempt already used', score: existing.value };
    }
    const add = (kind: string, value: number) => {
      const list = (this.boards[kind] ??= []);
      list.push({ uid: 'local', name: 'You', value, seed: run.seed, at: Date.now(), hasReplay: !!run.replay, meta: run.replay ? { replay: 1 } : undefined });
      list.sort((a, b) => (kind === 'fastestClear' ? a.value - b.value : b.value - a.value));
      list.length = Math.min(list.length, 20);
      if (run.replay) this.replays[`${kind}:${run.seed}`] = run.replay;
    };
    if (run.dailyKey) {
      add('daily:' + run.dailyKey, run.score);
    } else {
      add('highestScore', run.score);
      add('highestEra', run.highestEra);
      if (run.victory) add('fastestClear', run.timeSurvivedSec);
    }
    try {
      localStorage.setItem('chronofall.localboards', JSON.stringify(this.boards));
      localStorage.setItem('chronofall.localreplays', JSON.stringify(this.replays));
    } catch {
      /* ignore */
    }
    return { accepted: true, score: run.dailyKey ? run.score : undefined };
  }
  async fetchLeaderboard(kind: LeaderboardKind, dailyKey?: string, page = 0, pageSize = 10): Promise<LeaderboardPage> {
    const all = this.boards[kind === 'daily' ? 'daily:' + dailyKey : kind] ?? [];
    const start = Math.max(0, page) * pageSize;
    const ownIndex = all.findIndex((entry) => entry.uid === 'local');
    return {
      entries: all.slice(start, start + pageSize),
      page: Math.max(0, page),
      pageSize,
      total: all.length,
      ownEntry: ownIndex >= 0 ? all[ownIndex] : undefined,
      ownRank: ownIndex >= 0 ? ownIndex + 1 : undefined,
    };
  }
  async fetchReplay(kind: LeaderboardKind, _uid: string, dailyKey?: string, seed?: number): Promise<ReplayData | null> {
    const board = kind === 'daily' ? 'daily:' + dailyKey : kind;
    return this.replays[`${board}:${seed ?? ''}`] ?? null;
  }
}

export function firebaseConfigured(): boolean {
  const env = (import.meta as { env?: ImportMetaEnv }).env;
  if (!env) return false;
  return !!(env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_PROJECT_ID && env.VITE_FIREBASE_APP_ID);
}

/** Pick the hybrid cloud backend when configured, otherwise local. Never throws. */
export async function createBackend(): Promise<CloudBackend> {
  if (firebaseConfigured()) {
    try {
      const mod = await import('./firebaseBackend');
      const b = new mod.FirebaseBackend();
      await b.init();
      return b;
    } catch (e) {
      console.warn('Cloud backend failed to load; falling back to local', e);
    }
  }
  const b = new LocalBackend();
  await b.init();
  return b;
}

/** UTC date key for daily runs, e.g. "2026-09-18". The daily seed derives from it. */
export function dailyKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function dailySeed(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) ^ 0xda11;
}

/** HH:MM:SS remaining until the next UTC daily reset. */
export function dailyCountdown(date = new Date()): string {
  const next = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1);
  const total = Math.max(0, Math.ceil((next - date.getTime()) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}
